import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { getLocalizedContent, type LocalizedContent } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'
import {
  FirstNameSchema,
  LastNameSchema,
  EmailSchema,
} from '#app/utils/user-validation.ts'

export const INTENT_SEND_CONTACT_REQUEST = 'send-contact-request'
export const CONTACT_FORM_CONFIRMATION_CONTENT_TYPE =
  'Contact Form Confirmation'

const ContactFormSchema = z.object({
  firstname: FirstNameSchema,
  lastname: LastNameSchema,
  company: z.string().optional(),
  email: EmailSchema,
  hs_language: z.string(),
})

const getHubSpotApiUrl = () => {
  const baseUrl = `${process.env.HUBSPOT_FORMS_API_BASE_URL}`
  const actionPath = `integration/submit`
  const portalId = process.env.HUBSPOT_PORTAL_ID

  return `${baseUrl}/${actionPath}/${portalId}`
}

const getHubSpotPostBody = (data: any) => {
  const getFieldFromDataEntry = ([name, value]: [
    name: string,
    value?: any,
  ]) => {
    return {
      objectTypeId: '0-1',
      name,
      value: value ?? '',
    }
  }

  const fields = Object.entries(data).map(getFieldFromDataEntry)

  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fields }),
  }
}

export const sendContactRequest = async (formData: FormData) => {
  return parseWithZod(formData, {
    schema: () =>
      ContactFormSchema.transform(async (data: any) => {
        const baseHubSpotUrl = getHubSpotApiUrl()
        const formId = process.env.HUBSPOT_CONTACT_FORM_ID
        const contactFormApiUrl = `${baseHubSpotUrl}/${formId}`

        const postBody = getHubSpotPostBody(data)

        return fetch(contactFormApiUrl, postBody)
      }),
    async: true,
  })
}

export const ContactForm = ({
  headingTextContent,
  descriptionTextContent,
  submitButtonTextContent,
  confirmationTextContent,
  showSubmittedConfirmation,
}: {
  headingTextContent?: LocalizedContent
  descriptionTextContent?: LocalizedContent
  submitButtonTextContent?: LocalizedContent
  confirmationTextContent: LocalizedContent
  showSubmittedConfirmation?: boolean
}) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.language

  const [form, fields] = useForm({
    id: 'contact-form',
    constraint: getZodConstraint(ContactFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ContactFormSchema })
    },
    shouldRevalidate: 'onInput',
  })

  const HiddenFields = () => (
    <>
      <HoneypotInputs />
      <input
        type="hidden"
        readOnly
        name="intent"
        value={INTENT_SEND_CONTACT_REQUEST}
      />
      <input
        type="hidden"
        readOnly
        name="hs_language"
        value={locale}
        data-testid="contact-form-hidden-input-preferred-language"
      />
    </>
  )

  const textFieldClassName =
    'rounded-2 px-2 h-5 font-sans text-1 text-purple-7 lg:h-7 lg:text-2'

  const labelFirstname = t('contactForm.firstNameFieldLabel', locale)
  const labelLastname = t('contactForm.lastNameFieldLabel', locale)
  const labelCompany = t('contactForm.companyFieldLabel', locale)
  const labelEmail = t('contactForm.emailFieldLabel', locale)

  return (
    <div className="dark bg-purple-7 font-poppins lg:rounded-6 relative z-10 mt-7 flex w-full flex-col px-[56px] py-5 text-white lg:mt-9 lg:px-0 lg:py-8">
      <div className="top-[44px] left-[74px] hidden lg:absolute lg:block">
        <img src="/img/decorative/contact-form-star.svg" alt="" />
      </div>
      <div className="absolute bottom-[16px] left-0 w-[81px] lg:w-[224px]">
        <img src="/img/decorative/contact-form-shape-1.svg" alt="" />
      </div>
      <div className="absolute top-0 right-0 w-[95px] lg:w-[282px]">
        <img src="/img/decorative/contact-form-shape-2.svg" alt="" />
      </div>
      <div className="absolute right-[24px] bottom-[19px] w-[20px] lg:right-[101px] lg:bottom-[53px] lg:w-[38px]">
        <img src="/img/decorative/contact-form-sparkle.svg" alt="" />
      </div>
      <Form
        method="post"
        className="relative flex flex-col self-center lg:w-[502px]"
        {...getFormProps(form)}
      >
        <HiddenFields />

        {headingTextContent && (
          <h2 className="text-3 lg:text-5 text-center font-bold">
            {getLocalizedContent(headingTextContent, locale)}
          </h2>
        )}
        {descriptionTextContent && (
          <p className="text-2 lg:text-3 mt-4 text-center font-normal lg:mt-5">
            {getLocalizedContent(descriptionTextContent, locale)}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 lg:mt-6 lg:gap-5">
          <Field
            labelProps={{
              className: 'sr-only',
              children: labelFirstname,
            }}
            inputProps={{
              ...getInputProps(fields.firstname, { type: 'text' }),
              className: cn(textFieldClassName, 'aria-[invalid]:border-ruby-4'),
              placeholder: labelFirstname,
            }}
            errors={fields.firstname.errors}
          />
          <Field
            labelProps={{
              className: 'sr-only',
              children: labelLastname,
            }}
            inputProps={{
              ...getInputProps(fields.lastname, { type: 'text' }),
              className: textFieldClassName,
              placeholder: labelLastname,
            }}
            errors={fields.lastname.errors}
          />
        </div>

        <Field
          labelProps={{
            className: 'sr-only',
            children: labelCompany,
          }}
          inputProps={{
            ...getInputProps(fields.company, { type: 'text' }),
            className: textFieldClassName,
            placeholder: labelCompany,
          }}
          errors={fields.company.errors}
        />

        <Field
          labelProps={{
            className: 'sr-only',
            children: labelEmail,
          }}
          inputProps={{
            ...getInputProps(fields.email, { type: 'text' }),
            className: textFieldClassName,
            placeholder: labelEmail,
          }}
          errors={fields.email.errors}
        />

        {showSubmittedConfirmation ? (
          <p className="text-1 lg:text-2 text-center">
            {getLocalizedContent(confirmationTextContent, locale)}
          </p>
        ) : (
          <Button
            type="submit"
            variant="default"
            className={`text-1 lg:text-2 mx-auto w-fit px-3 font-normal`}
          >
            {getLocalizedContent(submitButtonTextContent, locale)}
          </Button>
        )}
      </Form>
    </div>
  )
}
